<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import ShipmentProtectionShippingComparison from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionShippingComparison.vue'
import ShipmentProtectionShippingBanner from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionShippingBanner.vue'
import ShipmentProtectionShippingUpgrade from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionShippingUpgrade.vue'
import TabbedFlowSteps from '@/components/TabbedFlowSteps.vue'
import ExcludedCoverageSection from '@/components/ExcludedCoverageSection.vue'
import type { PageContent } from '@/assets/ts/pages/shipment-protection-shipping'
import ShipmentProtectionFAQ from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFAQ.vue'
import ShipmentProtectionFooter from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFooter.vue'
import ShipmentProtectionShippingChoosePlusPlanBlock from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionShippingChoosePlusPlanBlock.vue'
import ShipmentProtectionShippingPlaneCard from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionShippingPlaneCard.vue'
import {
	getFlowIcons,
	createFlowHandlers,
} from '@/lib/shipment-protection-utils'
import { smoothScrollToElement, getButtonName } from '@/lib/insurance'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import getRedirectSeoLink from '@/lib/getSeoRedirect'
import { getCurrentGaPageName } from '@/lib/utils'
import { isGlobalSite } from '@/lib/checkService'

const props = defineProps<{
	pageContent: PageContent
}>()

const breadcrumbs = computed(() => [
	{
		displayName: window.pageContent.breadcrumbs?.home || '',
		categoryId: 'home',
		link: getSiteBaseUrl(),
	},
	{
		displayName: window.pageContent.breadcrumbs?.shipping || '',
		categoryId: 'shipping',
		link: `${getSiteBaseUrl()}/${getRedirectSeoLink('shipping')}`,
	},
	{
		displayName: window.pageContent.breadcrumbs?.shippingProtection || '',
		categoryId: 'shippingProtection',
		link: `${getSiteBaseUrl()}/${getRedirectSeoLink('protectionShipping')}`,
	},
])
const flowIcons = getFlowIcons('shipping')
const { handleButtonClick, handleTabClick } = createFlowHandlers(
	props.pageContent,
)

const comparisonRef = ref<InstanceType<
	typeof ShipmentProtectionShippingComparison
> | null>(null)

const handleComparisonRef = (theme: string, index: number) => {
	smoothScrollToElement(comparisonRef.value?.$el)

	const buttonName = getButtonName(theme)
	const globalButtonName = ['Learn More - Plus', 'Learn More - Basic']

	const gaButtonName = isGlobalSite()
		? globalButtonName[index]
		: `Learn More - ${buttonName}`

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: getCurrentGaPageName(),
		button_name: gaButtonName,
	})
}
</script>

<template>
	<PageBreadCrumb :breadcrumbs="breadcrumbs" />
	<ShipmentProtectionShippingBanner :page-content="props.pageContent" />
	<ShipmentProtectionShippingPlaneCard
		:page-content="props.pageContent"
		theme="shipping"
		@scroll-to-top="handleComparisonRef"
	/>
	<section class="bg-o-50 py-distant-acquaintances">
		<ShipmentProtectionShippingChoosePlusPlanBlock
			:page-content="props.pageContent"
		/>
	</section>

	<section class="py-distant-acquaintances">
		<ShipmentProtectionShippingComparison
			ref="comparisonRef"
			:page-content="props.pageContent"
		/>
	</section>

	<section class="bg-o-50 py-distant-acquaintances">
		<ShipmentProtectionShippingUpgrade :page-content="props.pageContent" />
	</section>

	<section
		v-if="props.pageContent.flowContent"
		class="bs-container py-distant-acquaintances"
	>
		<div class="col-span-12">
			<TabbedFlowSteps
				:content="props.pageContent.flowContent"
				:flow-icons="flowIcons"
				@button-click="handleButtonClick"
				@tab-click="handleTabClick"
			/>
		</div>
	</section>

	<ExcludedCoverageSection
		v-if="props.pageContent.excludedCoverage"
		:content="props.pageContent.excludedCoverage"
	/>

	<section class="bg-o-50 py-distant-acquaintances">
		<div class="bs-container">
			<div class="col-span-12">
				<ShipmentProtectionFAQ
					:content="props.pageContent.FAQ"
					theme="shipping"
				/>
			</div>
		</div>
	</section>

	<section class="bs-container py-distant-acquaintances">
		<div class="col-span-12">
			<ShipmentProtectionFooter
				theme="shipping"
				:footer="props.pageContent.footer"
			/>
		</div>
	</section>
</template>
